import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';

import { ArmyBuilderConfig } from '../../global/config';
import { HttpClientWithInFlightCache } from '../../global/httpClient';
import { ImageService } from '../../global/images/image.service';

export interface BrowseImageRecord {
    _id: string;
    s3Key: string;
    filename: string;
    contentType: string;
    gameId: string;
    category: string;
    size: number;
    usageCount: number;
    entityRefs: Array<{ entityId: string; entityType: string; createdAt: string }>;
    createdAt: string;
}

interface S3BrowseFile {
    key: string;
    size: number;
    lastModified: string;
    imageId?: string;
    usageCount: number;
}

interface S3BrowseResult {
    folders: string[];
    files: S3BrowseFile[];
}

/**
 * Modal that lets an admin browse existing images by S3 folder and select one.
 *
 * Starts at the server's IMAGE_ROOT_FOLDER and allows navigating into
 * subfolders. Navigation up stops at the root — the user cannot navigate
 * above the server-defined root.
 *
 * Returns `{ imageId: string }` on confirm, or `undefined` on cancel.
 */
@Component({
    standalone: true,
    selector: 'abs-file-browser-modal',
    imports: [CommonModule, IonicModule],
    template: `
        <ion-header class="neutral-header">
            <ion-toolbar class="neutral-toolbar">
                <ion-buttons slot="start">
                    <ion-button *ngIf="currentPrefix !== ''" (click)="navigateUp()" class="neutral-btn">
                        <ion-icon name="arrow-back-outline" slot="icon-only"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <div class="breadcrumb" slot="start">
                    <span class="crumb root" (click)="navigateTo('')">{{ rootFolder }}</span>
                    <ng-container *ngFor="let seg of pathSegments">
                        <span class="sep">/</span>
                        <span class="crumb">{{ seg }}</span>
                    </ng-container>
                </div>
                <ion-buttons slot="end">
                    <ion-button (click)="refresh()" class="neutral-btn" [disabled]="loading">
                        <ion-icon name="refresh-outline" slot="icon-only" [class.spinning]="loading"></ion-icon>
                    </ion-button>
                    <ion-button (click)="dismiss()" class="neutral-btn">Close</ion-button>
                </ion-buttons>
            </ion-toolbar>
            <ion-toolbar class="neutral-toolbar search-bar">
                <ion-searchbar
                    [debounce]="300"
                    placeholder="Filter by filename..."
                    (ionInput)="onSearch($event)"
                    class="neutral-searchbar"
                ></ion-searchbar>
            </ion-toolbar>
        </ion-header>

        <ion-content class="neutral-content ion-padding">
            <div class="loading" *ngIf="loading">
                <ion-spinner name="crescent"></ion-spinner>
                <span>Loading...</span>
            </div>

            <!-- Folders -->
            <div class="folder-list" *ngIf="!loading && folders.length > 0">
                <div class="folder-item" *ngFor="let folder of folders" (click)="navigateTo(folder)">
                    <ion-icon name="folder-outline"></ion-icon>
                    <span>{{ folderLabel(folder) }}</span>
                </div>
            </div>

            <div class="empty" *ngIf="!loading && filteredFiles.length === 0 && folders.length === 0">
                <ion-icon name="images-outline" size="large"></ion-icon>
                <p>This folder is empty.</p>
            </div>

            <!-- Files -->
            <div class="image-grid" *ngIf="!loading && filteredFiles.length > 0">
                <div
                    class="image-card"
                    *ngFor="let file of filteredFiles"
                    [class.selected]="file.imageId && selectedId === file.imageId"
                    (click)="file.imageId && select(file.imageId)"
                >
                    <div class="thumb">
                        <img *ngIf="file.imageId" [src]="getImageUrl(file.imageId)" (error)="onImageError($event)" />
                        <ion-icon *ngIf="!file.imageId" name="image-outline" class="no-meta-icon"></ion-icon>
                    </div>
                    <div class="meta">
                        <span class="filename" [title]="file.key">{{ fileLabel(file.key) }}</span>
                        <span class="detail">{{ formatSize(file.size) }}</span>
                        <span class="detail uses" [class.in-use]="file.usageCount > 0">
                            {{ file.usageCount }} {{ file.usageCount === 1 ? 'use' : 'uses' }}
                        </span>
                        <span class="detail no-meta" *ngIf="!file.imageId">No metadata</span>
                    </div>
                </div>
            </div>
        </ion-content>

        <ion-footer class="neutral-footer">
            <ion-toolbar class="neutral-toolbar">
                <ion-button expand="block" color="success" [disabled]="!selectedId" (click)="confirm()"> Select File </ion-button>
            </ion-toolbar>
        </ion-footer>
    `,
    styles: [
        `
            /* ── Neutral theme overrides ───────────────────────────────────
               This is a cross-app component. Override every Ionic CSS var
               that could be set by an app theme so it always renders cleanly.
            ────────────────────────────────────────────────────────────── */
            :host {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            .neutral-header,
            .neutral-footer {
                --background: #ffffff;
                background: #ffffff;
                --border-color: #e0e0e0;
            }

            .neutral-toolbar {
                --background: #ffffff;
                --color: #1a1a1a;
                --border-color: #e0e0e0;
            }

            .neutral-toolbar ion-title {
                color: #1a1a1a;
                font-size: 1rem;
                font-weight: 600;
            }

            .neutral-btn {
                --color: #1a1a1a;
                color: #1a1a1a;
            }

            .neutral-content {
                --background: #f5f5f5;
            }

            .neutral-searchbar {
                --background: #f0f0f0;
                --color: #1a1a1a;
                --placeholder-color: #888;
                --icon-color: #888;
                --border-radius: 8px;
            }

            /* ── Breadcrumb ─────────────────────────────────────────────── */
            .breadcrumb {
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 0 4px;
                font-size: 0.85rem;
                color: #1a1a1a;
                overflow: hidden;
            }

            .crumb {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 120px;
            }

            .crumb.root {
                cursor: pointer;
                color: #1a7ad4;
                font-weight: 600;
                max-width: 140px;
            }

            .sep {
                color: #aaa;
                flex-shrink: 0;
            }

            /* ── Folder list ────────────────────────────────────────────── */
            .folder-list {
                margin-bottom: 12px;
            }

            .folder-item {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 10px 12px;
                border-radius: 6px;
                cursor: pointer;
                font-weight: 500;
                color: #1a1a1a;
                background: #fff;
                margin-bottom: 4px;
            }

            .folder-item ion-icon {
                color: #f5a623;
                font-size: 1.3em;
            }

            .folder-item:hover {
                background: #ebebeb;
            }

            /* ── Loading / empty ────────────────────────────────────────── */
            .loading,
            .empty {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 40px;
                gap: 12px;
                color: #666;
            }

            /* ── Image grid ─────────────────────────────────────────────── */
            .image-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
                gap: 10px;
            }

            .image-card {
                border: 2px solid #e0e0e0;
                border-radius: 8px;
                overflow: hidden;
                cursor: pointer;
                transition: border-color 0.15s;
                background: #fff;
            }

            .image-card:hover {
                border-color: #999;
            }

            .image-card.selected {
                border-color: #1a7ad4;
                box-shadow: 0 0 0 2px #1a7ad4;
            }

            .thumb {
                width: 100%;
                aspect-ratio: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #f0f0f0;
                overflow: hidden;
            }

            .thumb img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
            }

            .no-meta-icon {
                font-size: 2.5em;
                color: #ccc;
            }

            .meta {
                padding: 6px 8px;
                display: flex;
                flex-direction: column;
                gap: 2px;
            }

            .filename {
                font-size: 0.78em;
                font-weight: 500;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                color: #1a1a1a;
            }

            .detail {
                font-size: 0.7em;
                color: #888;
            }

            .uses.in-use {
                color: #1a7ad4;
                font-weight: 500;
            }

            .no-meta {
                color: #cc8800;
                font-style: italic;
            }

            @keyframes spin {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(360deg);
                }
            }

            .spinning {
                animation: spin 0.7s linear infinite;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileBrowserModalComponent implements OnInit {
    private config = inject(ArmyBuilderConfig);
    private http = inject(HttpClientWithInFlightCache);
    private imageService = inject(ImageService);
    private modalController = inject(ModalController);
    private cdr = inject(ChangeDetectorRef);

    rootFolder = '';
    currentPrefix = '';
    pathSegments: string[] = [];
    folders: string[] = [];
    files: S3BrowseFile[] = [];
    filteredFiles: S3BrowseFile[] = [];
    loading = true;
    selectedId: string | null = null;

    private searchTerm = '';

    ngOnInit() {
        // Fetch the server root folder, then load it
        const rootUrl = `${this.config.apiBaseUrl}/images/admin/root-folder`;
        this.http.get(rootUrl).subscribe({
            next: (res: any) => {
                this.rootFolder = res.rootFolder || '';
                this.loadFolder('');
                this.cdr.markForCheck();
            },
            error: () => {
                this.rootFolder = '';
                this.loadFolder('');
                this.cdr.markForCheck();
            }
        });
    }

    navigateTo(prefix: string) {
        this.currentPrefix = prefix;
        this.pathSegments = prefix.split('/').filter(Boolean);
        this.loadFolder(prefix);
    }

    refresh() {
        this.loadFolder(this.currentPrefix);
    }

    navigateUp() {
        if (!this.currentPrefix) return;
        // Strip trailing slash, then remove last segment
        const trimmed = this.currentPrefix.replace(/\/$/, '');
        const parts = trimmed.split('/').filter(Boolean);
        parts.pop();
        const parent = parts.length ? parts.join('/') + '/' : '';
        this.navigateTo(parent);
    }

    getImageUrl(imageId: string): string | null {
        return this.imageService.url(imageId);
    }

    onSearch(event: any) {
        this.searchTerm = (event.detail?.value || '').toLowerCase();
        this.applyFilter();
    }

    select(imageId: string) {
        this.selectedId = this.selectedId === imageId ? null : imageId;
    }

    confirm() {
        if (this.selectedId) {
            this.modalController.dismiss({ imageId: this.selectedId });
        }
    }

    dismiss() {
        this.modalController.dismiss();
    }

    folderLabel(folder: string): string {
        return folder.replace(/\/$/, '').split('/').pop() || folder;
    }

    fileLabel(key: string): string {
        return key.split('/').pop() || key;
    }

    formatSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    onImageError(event: Event) {
        const img = event.target as HTMLImageElement;
        img.style.display = 'none';
    }

    private loadFolder(prefix: string) {
        this.loading = true;
        this.searchTerm = '';
        this.cdr.markForCheck();

        const params = new URLSearchParams();
        if (prefix) params.set('prefix', prefix);

        const url = `${this.config.apiBaseUrl}/images/admin/s3/list?${params}`;
        this.http.get(url).subscribe({
            next: (result: S3BrowseResult) => {
                this.folders = result.folders;
                this.files = result.files;
                this.applyFilter();
                this.loading = false;
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Failed to load folder:', err);
                this.loading = false;
                this.cdr.markForCheck();
            }
        });
    }

    private applyFilter() {
        if (!this.searchTerm) {
            this.filteredFiles = this.files;
        } else {
            this.filteredFiles = this.files.filter((f) => this.fileLabel(f.key).toLowerCase().includes(this.searchTerm));
        }
    }
}
